"use client"

import React from "react";

import Image from "next/image";

import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {CTAButton} from "@/shadcn/ui/button";
import {motion} from "framer-motion";

import {PageIQCapitalLaunchSchema} from "@/sanity/pages/PageIQCapitalLaunchTemplate";

import check from "@/public/iq-capital-launch/checkmark.svg";
import boxBg from "@/public/iq-capital-launch/box-bg.png";
import badge from "@/public/icons/Badge.svg";

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const fadeInDelay = (delay: number) => ({
  ...fadeIn,
  transition: { ...fadeIn.transition, delay }
});

const fadeInStagger = (index: number, baseDelay = 0.1) => ({
  ...fadeIn,
  transition: { ...fadeIn.transition, delay: baseDelay + (index * 0.1) }
});

export default function PricingSection({
  data,
  className
}: {
  data: PageIQCapitalLaunchSchema;
  className?: string;
}) {
  return (
    <section className={cn(
      "relative pb-[11rem] lg:pb-[20rem]",
      className
    )}>
      <div className="container flex flex-col items-center">
        <div className="w-[min(148.5rem,100%)] flex flex-col items-center">
          {data?.pricingTitle && (
            <motion.div
              {...fadeInDelay}
              className="richtext prose-p:w-[min(70rem,100%)] mb-[4rem]"
            >
              <PortableText
                value={data.pricingTitle}
                components={richTextComponent}
              />
            </motion.div>
          )}

          {data?.pricingItems && (
            <div className="w-full flex flex-col items-center lg:items-stretch lg:grid lg:grid-cols-3 gap-[2rem]">
              {data.pricingItems.map((item, index) => (
                <motion.div
                  key={index}
                  {...fadeInStagger(index, 0.1)}
                  className={`relative overflow-clip gradient-border-white-2 before:opacity-10 rounded-[2rem] lg:rounded-[2.5rem] px-[3rem] py-[4rem] flex flex-col justify-between w-[min(50rem,100%)] lg:w-auto ${item?.highlight ? 'shadow-[0_4px_28px_rgba(78,201,163,.2)]' : ''}`}
                >
                  <div className={`absolute inset-0 pointer-events-none ${item?.highlight ? 'bg-gradient-to-b from-[#023626] to-[#4EC9A3] to-[124%]' : 'bg-gradient-to-b from-white to-white/40 opacity-[.08]'}`} />

                  {item?.highlight && (
                    <Image
                      className="!w-[71.25rem] max-w-[unset] absolute left-1/2 top-0 -translate-x-1/2 pointer-events-none"
                      src={boxBg}
                      alt=""
                    />
                  )}

                  <div className="flex flex-col items-start mb-[4rem] lg:mb-[7rem] relative">
                    <div className="rounded-full border border-white/20 mb-[2.5rem]">
                      <p className="mb-0 leading-none text-white px-[1.53rem] py-[1rem] text-[1.75rem] font-medium inline-flex items-center">
                        {item?.chip}
                        {item?.highlight && (
                          <span className="inline-flex items-center gap-[.5rem] pl-[.75rem] ml-[1rem] border-l border-l-white">
                            <Image
                              className="!w-[2rem]"
                              src={badge}
                              alt=""
                            />
                            Bestseller
                          </span>
                        )}
                      </p>
                    </div>

                    <div className="flex items-end justify-start gap-[1.25rem] mb-[4rem]">
                      <p className="mb-0 text-white/60 leading-none text-[3rem] font-medium line-through">{item?.priceOld}</p>
                      <p className="mb-0 text-white leading-none text-[5rem] font-medium ">{item?.price}</p>
                    </div>

                    {item?.text && (
                      <div className={`richtext richtext-start prose-p:text-white ${item?.highlight ? 'opacity-100' : 'opacity-70'}`}>
                        <PortableText
                          value={item.text}
                          components={richTextComponent}
                        />
                      </div>
                    )}

                    <hr className="border-t [border-image:repeating-linear-gradient(to_right,rgba(255,255,255,.3)_0,rgba(255,255,255,.3)_8px,transparent_8px,transparent_16px)_1] w-full opacity-80 my-[3rem]" />

                    {item?.list && (
                      <ul className={`flex flex-col gap-[1rem] ${item?.highlight ? 'opacity-100' : 'opacity-70'}`}>
                        {item.list.map((listItem, index) => (
                          <li
                            key={index}
                            className="flex items-center gap-[1.25rem]"
                          >
                            <Image
                              className="!w-[3rem] flex-shrink-0 -translate-y-[.125rem]"
                              src={check}
                              alt=""
                            />

                            {listItem?.text && (
                              <div className="richtext richtext-start prose-p:text-white prose-p:font-medium">
                                <PortableText
                                  value={listItem.text}
                                  components={richTextComponent}
                                />
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {item?.cta && (
                    <CTAButton
                      className="relative"
                      data={item.cta}
                    />
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
